/**
 * «کیف پول هوش مصنوعی» against the real contract. Exact server-provided raw
 * token quota is shown only while a published flat token rate is active.
 * Legacy cost-plus wallets keep their money display. Internal credits are
 * never presented as provider tokens.
 *
 * Real purchase and top-up powered by ZarinPal payment gateway.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Modal,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
} from '@sakhtar/design-system/react';
import { useAiProvider } from '../connect/AiProviderContext.js';
import type {
  AiBalance,
  AiWelcomeCredit,
  AiTopupOrder,
  AiUsageRow,
  AiUsageScope,
} from '../connect/contract.ts';
import {
  executionLabel,
  formatMoneyAsTokens,
  groupDigits,
  isSettlementOpen,
  settlementLabel,
  settlementLines,
  totalUsageTokens,
  usageBreakdown,
} from '../connect/display.ts';
import { displayJalaliDateTime } from '../../../lib/date/displayJalali.js';
import { browserPaymentRecoveryStore, recoverWalletPayment } from '../connect/paymentRecovery.ts';
import { useAiResource } from './useAiResource.ts';
import { formatIntegerString, orderTokenQuota, topupAmount } from '../wallet/pricing.ts';
import { BuyCreditModal } from '../wallet/BuyCreditModal.js';
import './live.css';

const STATE_TONE = {
  settled: 'success',
  pending: 'warn',
  failed: 'danger',
  refunded: 'info',
} as const;

const ORDER_STATUS_MAP = {
  paid: { tone: 'success', label: 'پرداخت‌شده' },
  pending: { tone: 'warn', label: 'در انتظار پرداخت' },
  failed: { tone: 'danger', label: 'ناموفق' },
  cancelled: { tone: 'neutral', label: 'لغو شده' },
} as const;

export function LiveWallet() {
  const provider = useAiProvider();
  const balance = useAiResource(
    async (signal) => {
      const welcome = await provider.claimWelcomeCredit({ signal });
      return { balance: await provider.getBalance({ signal }), welcome };
    },
    [provider],
  );
  const orders = useAiResource((signal) => provider.getTopupOrders(20, { signal }), [provider]);

  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState<{
    type: 'success' | 'danger' | 'info';
    message: string;
    refId?: string | undefined;
  } | null>(null);

  const walletPath = typeof window === 'undefined' ? '' : window.location.pathname;
  const paymentStore = useMemo(() => browserPaymentRecoveryStore(walletPath), [walletPath]);
  const [verifyAttempt, setVerifyAttempt] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [canRetryPayment, setCanRetryPayment] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const controller = new AbortController();
    let active = true;
    setVerifying(true);
    recoverWalletPayment(provider, window.location.href, paymentStore, {
      signal: controller.signal,
    })
      .then(
        (recovery) => {
          if (!active || recovery === null) return;
          const res = recovery.result;
          setCanRetryPayment(res.status === 'pending');
          if (recovery.completedUrl !== null)
            window.history.replaceState({}, '', recovery.completedUrl);
          setPaymentNotice({
            type: res.status === 'paid' ? 'success' : res.status === 'failed' ? 'danger' : 'info',
            message:
              res.message ||
              (res.status === 'pending'
                ? 'نتیجه پرداخت هنوز مشخص نیست؛ می‌توانید دوباره بررسی کنید.'
                : 'نتیجه پرداخت دریافت شد.'),
            ...(res.refId === null ? {} : { refId: res.refId }),
          });
          balance.reload();
          orders.reload();
        },
        (err: unknown) => {
          if (!active) return;
          setCanRetryPayment(true);
          setPaymentNotice({
            type: 'info',
            message:
              err instanceof Error
                ? err.message
                : 'نتیجه پرداخت مشخص نشد. اطلاعات پیگیری حفظ شده است.',
          });
        },
      )
      .finally(() => {
        if (active) setVerifying(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [provider, paymentStore, verifyAttempt]);

  return (
    <div className="ai-live">
      {paymentNotice ? (
        <Alert
          tone={paymentNotice.type}
          title={
            paymentNotice.type === 'success'
              ? 'تراکنش موفق'
              : paymentNotice.type === 'info'
                ? 'پیگیری پرداخت'
                : 'تراکنش ناموفق'
          }
        >
          <div>
            <p>{paymentNotice.message}</p>
            {paymentNotice.refId ? (
              <p className="ai-live__ref">شماره پیگیری درگاه: {paymentNotice.refId}</p>
            ) : null}
            <div style={{ marginTop: 8 }}>
              {canRetryPayment ? (
                <V2Button
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={verifying}
                  onClick={() => setVerifyAttempt((value) => value + 1)}
                >
                  {verifying ? 'در حال بررسی…' : 'بررسی دوباره پرداخت'}
                </V2Button>
              ) : null}
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                onClick={() => setPaymentNotice(null)}
              >
                بستن
              </button>
            </div>
          </div>
        </Alert>
      ) : null}

      <V2Card>
        <V2CardHeader
          title="کیف پول هوش مصنوعی"
          subtitle="مانده، سهمیهٔ فعال و سوابق مصرف شرکت"
          icon="star"
          actions={
            <V2Button size="sm" variant="primary" onClick={() => setBuyModalOpen(true)}>
              خرید اعتبار
            </V2Button>
          }
        />
        <BalanceBody state={balance.state} reload={balance.reload} />
      </V2Card>

      <BuyCreditModal
        open={buyModalOpen}
        onClose={() => setBuyModalOpen(false)}
        onSuccess={() => {
          balance.reload();
          orders.reload();
        }}
      />

      <OrdersSection
        state={orders.state}
        reload={orders.reload}
        onBalanceChanged={balance.reload}
      />

      <Usage />
    </div>
  );
}

function BalanceBody({
  state,
  reload,
}: {
  state: ReturnType<
    typeof useAiResource<{ balance: AiBalance; welcome: AiWelcomeCredit }>
  >['state'];
  reload: () => void;
}) {
  if (state.status === 'loading')
    return (
      <div className="ai-live__pad" role="status">
        در حال دریافت مانده…
      </div>
    );
  if (state.status === 'error') {
    return (
      <div className="ai-live__pad ai-live__error" role="alert">
        <p>{state.error.message}</p>
        {state.error.requestId ? (
          <p className="ai-live__ref">کد پیگیری: {state.error.requestId}</p>
        ) : null}
        <div>
          <V2Button size="sm" variant="primary" onClick={reload}>
            تلاش دوباره
          </V2Button>
        </div>
      </div>
    );
  }
  const b = state.data.balance;
  const welcome = state.data.welcome;
  return (
    <>
      {welcome.status === 'granted' || welcome.status === 'already_granted' ? (
        <div className="ai-live__pad">
          <p>
            اعتبار هدیه آزمایشی شرکت:{' '}
            <strong className="sk-num">
              {welcome.tokens
                ? `${formatIntegerString(welcome.tokens)} توکن`
                : `${(BigInt(welcome.amountToman ?? '20000') * 5n).toLocaleString('fa-IR')} توکن`}
            </strong>
          </p>
          <p className="ai-live__hint">
            این هدیهٔ ۱۰۰ هزار توکنی یک‌بار برای شرکت فعال شده و بین تمام کاربران مجاز مشترک است. مانده قابل‌استفاده پس از
            مصرف در بخش موجودی به‌روز می‌شود.
          </p>
        </div>
      ) : welcome.status === 'configuration_required' ? (
        <div className="ai-live__pad" role="status">
          اعتبار هدیه هنوز ثبت نشده است؛ تنظیمات اعتبار هوش مصنوعی باید توسط پشتیبانی تکمیل شود.
        </div>
      ) : null}
      <dl className="ai-live-wallet__grid">
        {b.rawTokens ? (
          <>
            <div className="ai-live-wallet__stat">
              <dt>موجودی توکن (قابل‌استفاده)</dt>
              <dd className="sk-num">{formatIntegerString(b.rawTokens.available)} توکن</dd>
            </div>
            <div className="ai-live-wallet__stat">
              <dt>توکن‌های رزروشده</dt>
              <dd className="sk-num">{formatIntegerString(b.rawTokens.reserved)} توکن</dd>
            </div>
          </>
        ) : (
          <>
            <div className="ai-live-wallet__stat">
              <dt>موجودی توکن (قابل‌استفاده)</dt>
              <dd className="sk-num">{formatMoneyAsTokens(b.available)}</dd>
            </div>
            <div className="ai-live-wallet__stat">
              <dt>توکن‌های رزروشده</dt>
              <dd className="sk-num">{formatMoneyAsTokens(b.reserved)}</dd>
            </div>
          </>
        )}
      </dl>
      <div className="ai-live__pad">
        <p className="ai-live__hint">
          هر توکن واقعی ورودی یا خروجی، یک توکن مصرف می‌کند. کش و استدلالی که در شمارش اصلی
          آمده‌اند دوباره شمرده نمی‌شوند.
        </p>
      </div>
    </>
  );
}



function OrdersSection({
  state,
  reload,
  onBalanceChanged,
}: {
  state: ReturnType<typeof useAiResource<readonly AiTopupOrder[]>>['state'];
  reload: () => void;
  onBalanceChanged: () => void;
}) {
  const provider = useAiProvider();
  const [checkingOrder, setCheckingOrder] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  async function recheck(orderId: string) {
    if (checkingOrder !== null) return;
    setCheckingOrder(orderId);
    setNotice(null);
    try {
      const result = await provider.reconcileTopup(orderId);
      setNotice(result.message);
      if (result.status === 'paid')
        browserPaymentRecoveryStore(window.location.pathname).clear(orderId);
      reload();
      onBalanceChanged();
    } catch (error: unknown) {
      setNotice(
        error instanceof Error ? error.message : 'بررسی انجام نشد؛ امکان تلاش دوباره وجود دارد.',
      );
    } finally {
      setCheckingOrder(null);
    }
  }
  return (
    <V2Card>
      <V2CardHeader
        title="سوابق خرید اعتبار"
        subtitle="تراکنش‌های خرید و شارژ اعتبار هوش مصنوعی از طریق درگاه پرداخت."
        actions={
          <V2Button size="sm" variant="secondary" onClick={reload}>
            به‌روزرسانی
          </V2Button>
        }
      />
      {notice ? (
        <p className="ai-live__pad" role="status">
          {notice}
        </p>
      ) : null}
      {state.status === 'loading' ? (
        <div className="ai-live__pad" role="status">
          در حال دریافت سوابق تراکنش‌ها…
        </div>
      ) : state.status === 'error' ? (
        <div className="ai-live__pad ai-live__error" role="alert">
          <p>{state.error.message}</p>
        </div>
      ) : state.data.length === 0 ? (
        <V2EmptyState compact icon="info" title="هنوز خریدی ثبت نشده است" />
      ) : (
        <div className="ai-live__table-wrap">
          <table className="ai-live__table">
            <thead>
              <tr>
                <th>زمان</th>
                <th>سهمیه توکن</th>
                <th>وضعیت</th>
                <th>شماره پیگیری</th>
                <th>شماره کارت</th>
                <th>پیگیری</th>
              </tr>
            </thead>
            <tbody>
              {state.data.map((order) => {
                const conf = ORDER_STATUS_MAP[order.status] ?? {
                  tone: 'neutral',
                  label: order.status,
                };
                return (
                  <tr key={order.orderId}>
                    <td className="sk-num" data-label="زمان">
                      {displayJalaliDateTime(order.createdAt)}
                    </td>
                    <td className="sk-num" data-label="سهمیه توکن">
                      {orderTokenQuota(order) === null
                        ? 'نامشخص'
                        : `${formatIntegerString(orderTokenQuota(order) as string)} توکن`}
                    </td>
                    <td data-label="وضعیت">
                      <V2Badge tone={conf.tone}>{conf.label}</V2Badge>
                    </td>
                    <td className="sk-num" data-label="شماره پیگیری">
                      {order.refId ?? '—'}
                    </td>
                    <td className="sk-num" data-label="شماره کارت">
                      {order.cardPan ?? '—'}
                    </td>
                    <td data-label="پیگیری">
                      {order.status !== 'paid' && order.authority !== null ? (
                        <V2Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={checkingOrder !== null}
                          onClick={() => {
                            void recheck(order.orderId);
                          }}
                        >
                          {checkingOrder === order.orderId
                            ? 'در حال بررسی…'
                            : 'بررسی دوباره پرداخت'}
                        </V2Button>
                      ) : order.status === 'pending' ? (
                        'در صورت پرداخت و باقی‌ماندن این وضعیت، با پشتیبانی تماس بگیرید.'
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </V2Card>
  );
}

function Usage() {
  const provider = useAiProvider();
  const [scope, setScope] = useState<AiUsageScope>('own');
  const usage = useAiResource((signal) => provider.getUsage(scope, { signal }), [provider, scope]);

  return (
    <V2Card>
      <V2CardHeader
        title="مصرف"
        subtitle="هر ردیف همان وضعیت و سهمیهٔ مصرفی اعلام‌شدهٔ سرور است."
        actions={
          <div className="ai-live__row">
            <V2Button
              size="sm"
              variant={scope === 'own' ? 'primary' : 'secondary'}
              onClick={() => setScope('own')}
            >
              مصرف من
            </V2Button>
            <V2Button
              size="sm"
              variant={scope === 'all' ? 'primary' : 'secondary'}
              onClick={() => setScope('all')}
            >
              همهٔ کاربران
            </V2Button>
          </div>
        }
      />
      {usage.state.status === 'loading' ? (
        <div className="ai-live__pad" role="status">
          در حال دریافت مصرف…
        </div>
      ) : usage.state.status === 'error' ? (
        <div className="ai-live__pad ai-live__error" role="alert">
          <p>{usage.state.error.message}</p>
          {usage.state.error.requestId ? (
            <p className="ai-live__ref">کد پیگیری: {usage.state.error.requestId}</p>
          ) : null}
          <div>
            <V2Button size="sm" variant="primary" onClick={usage.reload}>
              تلاش دوباره
            </V2Button>
          </div>
        </div>
      ) : usage.state.data.items.length === 0 ? (
        <V2EmptyState compact icon="info" title="مصرفی ثبت نشده است" />
      ) : (
        <div className="ai-live__table-wrap">
          <table className="ai-live__table">
            <thead>
              <tr>
                <th>زمان</th>
                <th>وضعیت</th>
                <th>مصرف توکن</th>
                <th>تسویه سهمیه</th>
                <th>جزئیات مصرف</th>
              </tr>
            </thead>
            <tbody>
              {usage.state.data.items.map((row) => (
                <UsageRow key={row.turnId} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </V2Card>
  );
}

function UsageRow({ row }: { row: AiUsageRow }) {
  const detail = usageBreakdown(row.usage);
  const total = totalUsageTokens(row.usage);
  const lines = settlementLines(row.settlement);
  const open = isSettlementOpen(row.settlement);
  return (
    <tr>
      <td className="sk-num" data-label="زمان">
        {displayJalaliDateTime(row.createdAt)}
      </td>
      <td data-label="وضعیت">
        <V2Badge tone={STATE_TONE[row.state]}>{row.stateLabel}</V2Badge>
        <div className="ai-live__hint">{executionLabel(row.execution)}</div>
      </td>
      <td className="sk-num" data-label="مصرف توکن">
        {total === null ? (
          <span className="ai-live__hint">در انتظار تعیین</span>
        ) : (
          `${groupDigits(total)} توکن`
        )}
      </td>
      <td className="sk-num" data-label="تسویه سهمیه">
        <div>
          {row.charge === null
            ? 'در انتظار تعیین'
            : row.charge.toman !== null
              ? `${groupDigits((BigInt(row.charge.toman) * 5n).toString())} توکن`
              : '—'}
        </div>
        <div className="ai-live__hint">{settlementLabel(row.settlement)}</div>
        {lines.map((line) => (
          <div key={line.label} className="ai-live__hint sk-num">
            {line.label}: {line.value}
          </div>
        ))}
        {open ? (
          <div className="ai-live__hint">پایان رزرو فقط با اعلام سرور مشخص می‌شود.</div>
        ) : null}
      </td>
      <td data-label="جزئیات مصرف">
        <details>
          <summary>نمایش</summary>
          <dl className="ai-live__facts">
            {detail.map((d) => (
              <div key={d.key} className="ai-live__fact">
                <dt>{d.label}</dt>
                <dd className="sk-num">{d.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      </td>
    </tr>
  );
}
